import { useCallback, useEffect } from "react";

import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { isWebRTCSupported } from "../utils/is-webrtc-supported";

type HasWebRTCPermission = {
  requestCameraPermission: () => Promise<void>;
  requestMicrophonePermission: () => Promise<void>;
};

// this function was partially copied from classview v1.
export function useHasWebRTCPermission(): HasWebRTCPermission {
  const setCameraHasPermission = useVidyoStore(
    (state) => state.camera.setHasPermission,
  );
  const setMicrophoneHasPermission = useVidyoStore(
    (state) => state.microphone.setHasPermission,
  );

  const nbOfCameras = useVidyoStore((state) => state.camera.available.length);
  const nbOfMicrophones = useVidyoStore(
    (state) => state.microphone.available.length,
  );

  const askForCameraPermission = useCallback(async () => {
    if (nbOfCameras === 0) return;
    try {
      const tryWebRTC = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      // Stop all tracks immediately to release the devices. This is necessary because:
      // 1. We only need to check permissions, not keep the devices active
      // 2. Vidyo SDK will need to access these devices later
      // 3. We need to ensure devices are properly released before Vidyo tries to use them
      // 4. Prevents the device active indicator (camera/mic light) from staying on unnecessarily
      tryWebRTC.getTracks().forEach((d) => d.stop());

      // Even though stop() returns immediately, the actual device release is asynchronous.
      // The 250ms timeout ensures the device is fully released and ready for Vidyo to use.
      window.setTimeout(() => {
        setCameraHasPermission(true);
      }, 250);
    } catch (e) {
      console.error(e);
      setCameraHasPermission(false);
    }
  }, [nbOfCameras, setCameraHasPermission]);

  const askForMicrophonePermission = useCallback(async () => {
    if (nbOfMicrophones === 0) return;
    try {
      const tryWebRTC = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      tryWebRTC.getTracks().forEach((d) => d.stop());
      window.setTimeout(() => {
        setMicrophoneHasPermission(true);
      }, 250);
    } catch (e) {
      console.error(e);
      setMicrophoneHasPermission(false);
    }
  }, [nbOfMicrophones, setMicrophoneHasPermission]);

  useEffect(() => {
    if (!isWebRTCSupported) return;

    askForCameraPermission();
    askForMicrophonePermission();
  }, [askForCameraPermission, askForMicrophonePermission]);

  return {
    requestCameraPermission: askForCameraPermission,
    requestMicrophonePermission: askForMicrophonePermission,
  };
}
